<template>
  <CenteredPageContainer :class="$style.page">
    <LoadingIndicator :class="$style.loading" v-if="loading" />
    <section
      :class="[$style.page__section, $style.section]"
      v-else-if="term != null"
    >
      <header>
        <h1 :class="$style.page__title">{{ term.title }}</h1>
      </header>
      <AppMarkdown
        :class="[$style.page__body, $style.body]"
        :body="term.body"
      />
    </section>
  </CenteredPageContainer>
</template>

<script lang="ts">
export const TermTypePathNameMap = {
  [TermType.PrivacyPolicy]: 'privacy-policy',
  [TermType.TermsOfService]: 'terms-of-service',
  [TermType.SecrecyOfCommunications]: 'secrecy-of-communications',
} as const satisfies Record<TermType, string>

const ReversedTermTypePathNameMap = Object.fromEntries(
  Object.entries(TermTypePathNameMap).map(([key, value]) => [value, key]) as [
    string,
    TermType,
  ][]
)
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import { useRouter, useRoute } from 'vue-router'

import AppMarkdown from '@/components/atoms/AppMarkdown.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import { useFetchTermPageQuery } from './TermPage.generated'
import { TermType } from '@/types/schema.generated'

const router = useRouter()
const route = useRoute('termDetail')
const { termTypeName } = route.params
const termType = computed<Maybe<TermType>>(() => {
  if (!termTypeName || !ReversedTermTypePathNameMap[termTypeName]) {
    router.replace({ name: 'errorNotFound' })
    return
  }
  return ReversedTermTypePathNameMap[termTypeName]
})

const { result, loading } = useFetchTermPageQuery(() => ({
  termType: termType.value ?? TermType.PrivacyPolicy,
}))
const term = computed(() => result.value?.term)

useHead({
  title: () => (term.value != null ? term.value.title : null),
})
</script>

<style lang="scss" module>
.section {
  padding: 32px 40px;
  background: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-24;
}

.page {
  &__section {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__title {
    color: $color-text-and-object-high-emphasis;
    font-size: 36px;
    font-weight: bold;
    line-height: 1.5;

    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  &__body {
    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }
}

.body {
  h1,
  h2,
  h3 {
    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }
  }

  p {
    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }
  }

  /* stylelint-disable no-descending-specificity */
  ol {
    padding-left: 2em;

    li {
      list-style: decimal;
    }

    ul,
    ol {
      margin-top: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 2em;
    }
  }

  ul {
    padding-left: 1.5em;

    li {
      list-style: disc;

      &:nth-child(n + 2) {
        margin-top: 0.5em;
      }

      li {
        list-style: circle;
      }
    }

    ul,
    ol {
      margin-top: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 2em;
    }

    ul:nth-child(n + 2) {
      margin-top: 0.5em;
    }
  }

  /* stylelint-enable no-descending-specificity */

  hr {
    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }
  }

  img {
    max-width: 100%;
  }

  table {
    border-spacing: 0;
    border-collapse: collapse;
    display: block;

    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }

    th,
    td {
      padding: 6px 13px;
      border: 1px solid black;
    }

    tr {
      border: 1px solid black;
    }
  }
}
</style>
